import { useQuery } from "@tanstack/react-query";
import SupabasePool from "@/lib/supabaseClient";
import ProjectListItem from "./ProjectListItem";

import { useRef, useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import HomeSectionDescription from "@/page/Home/components/home-section-description";
import HomeSectionTitle from "@/page/Home/components/home-section-title";
import FadeInAnimation from "@/components/animations/FadeInAnimation";
import LoadingSpiner from "@/components/ui/loading-spiner";

import ProjectFirstItem from "./components/Project-other-item";
import ProjectGraph from "./components/project-graph";
import { ProjectPostProps } from "@/type/ProjectTypes";
import { Button } from "@/components/ui/button";
import { useAuthentication } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";

const ProjectList = () => {
  const location = useLocation();
  const nav = useNavigate();
  const hasProcessedState = useRef(false);
  const [openProjectId, setOpenProjectId] = useState<string | null>(null);
  const authentication = useAuthentication();

  const { data, isSuccess, isLoading } = useQuery<ProjectPostProps[]>({
    queryKey: ["project-list"],
    queryFn: async () => {
      const { data, error } = await SupabasePool.getInstance()
        .from("project_meta")
        .select(
          `
            *,
            project_meta_stack(project_stack(*)),
            project_pin(*)
          `
        )
        .order("id", { ascending: false });

      if (error)
        throw new Error(
          `데이터를 불러오는 중 에러가 발생했습니다: ${error.message}`
        );

      return data || [];
    },
    staleTime: Infinity,
  });

  /* URL #id 오픈 처리 */
  useEffect(() => {
    if (
      location.state?.openProjectId &&
      isSuccess &&
      !hasProcessedState.current
    ) {
      hasProcessedState.current = true;
      const projectId = location.state.openProjectId;
      window.history.replaceState({}, "", "/work");
      window.history.pushState({}, "", `/work#${projectId}`);
      setOpenProjectId(projectId);
    }
  }, [location.state, isSuccess, nav]);

  useEffect(() => {
    return () => {
      hasProcessedState.current = false;
    };
  }, []);

  const listData = useMemo(() => {
    return data && data.filter((e) => !e.project_pin);
  }, [data]);

  return (
    <div className="flex flex-col grid-layout pt-50 md:pt-60 z-5">
      {/* ------------------------------- */}
      {/* 헤더 영역 */}
      {/* ------------------------------- */}
      <section className="grid grid-cols-1  pb-8 items-start">
        <HomeSectionTitle className="text-zinc-50 text-5xl lg:text-6xl leading-14 mb-10">
          Projects
        </HomeSectionTitle>
        <HomeSectionDescription className="text-zinc-400 leading-relaxed mb-12 flex flex-col items-start ">
          <p className="text-base max-w-[620px] mt-5 mb-2">
            React·Next 기반의 최신 프로젝트부터 초기 퍼블리싱 작업까지 포함한
            전체 작업 목록입니다. 비공개 또는 협업 정책에 따라 공유가 제한된
            작업은 게시하지 않습니다.
          </p>{" "}
          {authentication && (
            <Button className="text-xs" onClick={() => nav("/work/write")}>
              프로젝트 등록하기
            </Button>
          )}
          {data && !isLoading && <ProjectGraph data={data} />}
          {isLoading && (
            <div className="flex gap-5  mt-4">
              <Skeleton className="w-10 h-6" />
              <div className="flex gap-2">
                <Skeleton className="w-50 h-6" />
              </div>
            </div>
          )}
        </HomeSectionDescription>{" "}
      </section>

      <FadeInAnimation className="w-full  ">
        <ProjectFirstItem />
      </FadeInAnimation>

      <section className="w-full mt-30 pt-16  border-zinc-700/40">
        {/* [!!] Pinned 섹션과 구분을 위한 상단 여백(mt), 안쪽 여백(pt), 구분선(border-t) 추가 */}

        {/* Left Info -> Top Info (스티키 제거, 그리드 제거) */}
        <div className="mb-5">
          {/* 리스트와 간격을 주기 위해 mb-12 추가 */}
          <aside className="flex flex-col items-start">
            <HomeSectionDescription className="text-zinc-400 leading-relaxed mb-6">
              <h1 className="text-zinc-50 text-3xl flex gap-2 items-center font-semibold">
                Other Projects
              </h1>
              <p className="text-base leading-relaxed">
                Pinned를 제외한 전체 프로젝트 목록입니다.
              </p>{" "}
              {/* <ProjectViewMode /> */}
            </HomeSectionDescription>
          </aside>
        </div>

        {/* Right list -> Bottom list (그리드 제거) */}
        <ul className=" py-4 ">
          <FadeInAnimation className="grid lg:grid-cols-3 gap-5 lg:gap-10 ">
            {isSuccess && data ? (
              data.length > 0 ? (
                listData!
                  .filter((e) => !e.project_pin)
                  .map((project, idx) => (
                    <li key={`${project.id}:${idx}`}>
                      <ProjectListItem
                        project={project}
                        open={project.id === openProjectId}
                        idx={idx}
                        onOpenChange={(isOpen) =>
                          setOpenProjectId(isOpen ? project.id : null)
                        }
                        // [!!] 여기에 'Other'용으로 만든 컴팩트한 스타일을 적용해야 합니다.
                        // 예: <ProjectListItem layout="compact" ... />
                      />
                    </li>
                  ))
              ) : (
                <div className="col-span-full text-center text-zinc-500 py-10">
                  등록된 프로젝트가 없습니다.
                </div>
              )
            ) : (
              <LoadingSpiner />
            )}
          </FadeInAnimation>
        </ul>
      </section>
    </div>
  );
};

export default ProjectList;
